import { type EntityResolution, isEntityKind, plural, type SchemaSnapshot } from '@hoopoe/shared';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft, Check, FileSearch, Plus, X } from 'lucide-react';
import { useId, useState } from 'react';
import { DataNumber, DataText } from '../components/data/data-text';
import { EntityLink } from '../components/entities/entity-link';
import { EmptyState } from '../components/guidance/empty-state';
import { FieldHelp } from '../components/guidance/field-help';
import { Tip } from '../components/guidance/tip';
import { useScreenTour } from '../components/guidance/tour';
import { PageHeader } from '../components/layout/app-shell';
import { dateOf } from '../components/review/inbox';
import { Button } from '../components/ui/button';
import { ApiError } from '../lib/api';
import { cn } from '../lib/cn';
import { type BatchCard, DECISION_TEXT, useBatchReview, useInboxWrite } from '../lib/review-inbox';
import { useSchema } from '../lib/schema';

// Batch review by entity (PLAN §5 step 6b; task 21): the files of one upload still at the entity check, folded into
// one card per entity (the same organisation named in 34 of 60 profile sheets is one card, not 34). A decision on a
// card applies to every file that mentions it; each file's own review shows it, and stays available for the rest.

export const Route = createFileRoute('/review/batch/$batchId')({ component: BatchReviewPage });

const TOUR = {
  id: 'review-batch',
  steps: [
    {
      target: '[data-tour="batch-cards"]',
      title: 'One card per entity',
      body: 'An entity named in several files of this upload is one card listing every file. Decide once; the decision is saved in each file’s review.',
      learnMore: 'review-inbox',
    },
    {
      target: '[data-tour="batch-files"]',
      title: 'The files',
      body: 'Open a file to finish its review: facts and relations, then the AI re-check.',
    },
  ],
};

function BatchReviewPage() {
  useScreenTour(TOUR);
  const { batchId } = Route.useParams();
  const view = useBatchReview(batchId);
  const { data: snap } = useSchema();
  const write = useInboxWrite();
  const [left, setLeft] = useState<{ candidateId: string; reason: string }[]>([]);
  const [onlyPending, setOnlyPending] = useState(true);
  const back = (
    <Link to="/review" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
      <ArrowLeft className="size-4" aria-hidden="true" /> Review Inbox
    </Link>
  );
  if (view.isError)
    return (
      <div className="space-y-4">
        {back}
        <EmptyState icon={FileSearch} title="Nothing to review here" learnMore="review-inbox">
          {view.error instanceof ApiError ? view.error.message : 'Try again in a moment.'}
        </EmptyState>
      </div>
    );
  if (!view.data || !snap) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const { batch, documents, cards, progress } = view.data;
  const decide = async (card: BatchCard, resolution: EntityResolution) => {
    const ids = card.sources.filter(s => s.decision === 'pending').map(s => s.candidateId);
    const out = (await write.mutateAsync({ kind: 'batch', batchId, candidateIds: ids, resolution }).catch(() => null)) as {
      left: { candidateId: string; reason: string }[];
    } | null;
    setLeft(out?.left ?? []);
  };
  const shown = onlyPending ? cards.filter(c => c.pending > 0) : cards;
  return (
    <div className="space-y-4">
      {back}
      <PageHeader
        title={`Review by entity: ${batch.label}`}
        description={[plural(documents.length, 'file'), batch.uploader ? `uploaded by ${batch.uploader}` : null, dateOf(batch.createdAt)]
          .filter(Boolean)
          .join(' · ')}
      />
      <Tip id="review-batch" learnMore="review-inbox">
        Each card is one entity, with every file of this upload that names it. Match it to an existing record, create it once for all the
        files, or reject it. Then open each file to check its facts.
      </Tip>
      <section className="flex flex-wrap items-center gap-3 rounded-lg border bg-surface px-3 py-2" aria-label="Batch progress">
        <span className="text-sm" data-testid="batch-progress">
          <DataNumber>{progress.cards - progress.pending}</DataNumber> of <DataNumber>{progress.cards}</DataNumber> entities decided
        </span>
        <progress
          className="h-2 w-40 accent-foreground"
          max={Math.max(progress.cards, 1)}
          value={progress.cards - progress.pending}
          aria-label="Entities decided"
        />
        <span className="text-xs text-muted-foreground">
          <DataNumber>{progress.shared}</DataNumber> named in more than one file
        </span>
        <label className="ms-auto flex items-center gap-2 text-sm">
          <input type="checkbox" checked={onlyPending} onChange={e => setOnlyPending(e.target.checked)} />
          Only entities to decide
        </label>
      </section>
      {write.error && (
        <p role="alert" className="text-sm text-danger-foreground">
          {write.error}
        </p>
      )}
      {left.length > 0 && (
        <div role="status" className="rounded-md border border-warning px-3 py-2 text-sm">
          <p className="font-medium">Some files were left for their own review:</p>
          <ul className="list-disc ps-5">
            {[...new Set(left.map(l => l.reason))].map(r => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        </div>
      )}
      <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div data-tour="batch-cards" className="space-y-2">
          {shown.length === 0 ? (
            <EmptyState icon={Check} title="Every entity has a decision" learnMore="review-inbox">
              Open each file to continue with its facts and relations.
            </EmptyState>
          ) : (
            <ul className="space-y-2">
              {shown.map(card => (
                <Card key={card.key} card={card} snap={snap} busy={write.isPending} onDecide={r => decide(card, r)} />
              ))}
            </ul>
          )}
        </div>
        <section data-tour="batch-files" aria-labelledby="batch-files" className="space-y-2 rounded-lg border bg-surface p-3">
          <h2 id="batch-files" className="font-semibold">
            Files in this upload
          </h2>
          <ul className="space-y-1 text-sm">
            {documents.map(d => (
              <li key={d.id} className="flex flex-wrap items-center gap-2">
                <Link to="/review/$id" params={{ id: d.id }} className="min-w-0 truncate underline-offset-2 hover:underline">
                  <DataText>{d.filename}</DataText>
                </Link>
                {d.why && <span className="text-xs text-muted-foreground">{d.why}</span>}
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}

function Card({
  card,
  snap,
  busy,
  onDecide,
}: {
  card: BatchCard;
  snap: SchemaSnapshot;
  busy: boolean;
  onDecide: (r: EntityResolution) => void;
}) {
  const typeId = useId();
  const kind = isEntityKind(card.kind) ? card.kind : 'actor';
  const types = snap.types.filter(t => t.kind === kind && !t.archived);
  const [typeKey, setTypeKey] = useState(card.typeKey ?? types[0]?.key ?? '');
  const pending = card.pending > 0;
  const decided = card.sources.filter(s => s.decision !== 'pending');
  return (
    <li className={cn('space-y-2 rounded-lg border bg-surface p-3', !pending && 'opacity-80')} aria-label={card.name} data-card={card.key}>
      <div className="flex flex-wrap items-center gap-2">
        {card.entity ? <EntityLink schema={snap} entity={card.entity} /> : <DataText className="font-medium">{card.name}</DataText>}
        <span className="text-xs text-muted-foreground">
          {card.typeText ? <DataText>{card.typeText}</DataText> : null}
          {card.typeText ? ' · ' : ''}
          in <DataNumber>{plural(card.documents, 'file')}</DataNumber>
        </span>
        {card.crossScript && <span className="rounded bg-muted px-1.5 py-0.5 text-xs">Same person? check</span>}
        <span
          className={cn(
            'ms-auto rounded-full border px-2 py-0.5 text-xs',
            pending ? 'border-warning text-warning-foreground' : 'border-success text-success-foreground',
          )}
        >
          {pending ? `${card.pending} to decide` : 'Decided'}
        </span>
      </div>
      <details className="text-sm">
        <summary className="cursor-pointer text-muted-foreground">Where it appears ({card.sources.length})</summary>
        <ul className="mt-1 divide-y rounded-md border">
          {card.sources.map(s => (
            <li key={s.candidateId} className="flex flex-wrap items-start gap-2 p-2">
              <div className="min-w-0 flex-1">
                <Link to="/review/$id" params={{ id: s.documentId }} className="underline-offset-2 hover:underline">
                  <DataText>{s.filename}</DataText>
                </Link>
                <span className="text-muted-foreground"> · </span>
                <DataText>{s.name}</DataText>
                {s.quote && (
                  <p className="text-xs text-muted-foreground">
                    “<DataText>{s.quote}</DataText>”
                  </p>
                )}
              </div>
              <span className="text-xs text-muted-foreground">{DECISION_TEXT[s.decision]}</span>
            </li>
          ))}
        </ul>
      </details>
      {pending && (
        <div className="flex flex-wrap items-end gap-2">
          {card.suggestions.slice(0, 3).map(s => (
            <Button
              key={s.entityId}
              size="sm"
              variant={s.preselected ? 'primary' : 'outline'}
              disabled={busy}
              onClick={() => onDecide({ action: 'match', entityId: s.entityId })}
            >
              <Check className="size-4" aria-hidden="true" /> Match {s.entity?.displayName ?? s.displayName} (
              <DataNumber>{Math.round(s.score * 100)}%</DataNumber>)
            </Button>
          ))}
          <span className="flex items-end gap-1">
            <span className="space-y-1">
              <span className="flex items-center gap-1">
                <label htmlFor={typeId} className="text-xs font-medium">
                  Type
                </label>
                <FieldHelp
                  label="Type"
                  help="The type the new entity gets. It is created once and every file of this upload is matched to it."
                  learnMore="review-inbox"
                />
              </span>
              <select
                id={typeId}
                value={typeKey}
                onChange={e => setTypeKey(e.target.value)}
                className="h-8 rounded-md border border-input bg-surface px-2 text-sm"
              >
                {types.map(t => (
                  <option key={t.key} value={t.key}>
                    {t.label}
                  </option>
                ))}
              </select>
            </span>
            <Button
              size="sm"
              variant={card.suggestions.some(s => s.preselected) ? 'outline' : 'primary'}
              disabled={busy || !typeKey}
              onClick={() => onDecide({ action: 'new', typeKey, names: [{ value: card.name, lang: null, kind: 'primary' }] })}
            >
              <Plus className="size-4" aria-hidden="true" /> Create for {plural(card.pending, 'file')}
            </Button>
          </span>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => onDecide({ action: 'reject' })}>
            <X className="size-4" aria-hidden="true" /> Reject
          </Button>
        </div>
      )}
      {!pending && decided.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {[...new Set(decided.map(s => DECISION_TEXT[s.decision]))].join(', ')}. Change it in each file’s review.
        </p>
      )}
    </li>
  );
}
